/**
 * Authentication Provider
 *
 * Component-only module (Fast Refresh boundary). The context object and
 * `useAuth` live in `AuthContext.ts`.
 *
 * Provides authentication state and methods to the application.
 * Uses Microsoft Entra External ID for all authentication flows.
 *
 * Authentication Flow:
 * 1. User clicks "Sign In / Sign Up" on LoginPage
 * 2. App redirects to External ID's hosted login page
 * 3. User authenticates (email/password or social provider)
 * 4. External ID redirects back to /auth/callback
 * 5. AuthContext processes the redirect and syncs user to backend
 * 6. User is navigated to dashboard
 *
 * Token Refresh & Session Expiration:
 * - Tokens are automatically refreshed via MSAL acquireTokenSilent
 * - When session expires, handleAuthExpiration clears state and redirects to login
 * - API client attempts refresh on 401, triggers expiration callback on failure
 *
 * External ID handles:
 * - Sign-in with email/password
 * - Self-service sign-up
 * - Social login (Google, Facebook, Microsoft)
 * - Password reset
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { getAuthConfigFromEnv } from '../config/authConfig';
import type { B2CConfig, AuthModeConfig } from '../config/authConfig';
import { type AuthFlow, AUTH_ROUTES, setReturnTo, isPublicPath, sanitizeReturnToPath } from '../constants/auth';
import {
  SUPER_ADMIN,
  hasAnyAdminRole,
  hasContentManagementAccess,
  hasSupportAccess,
} from '../constants/roles';
import { clearSessionStorageForLogout } from '../services/sessionCleanupService';
import { flushAcquisitionAttributionToServer } from '../services/flushAcquisitionAttribution';
import { flushCookieConsentToServer } from '../services/flushCookieConsent';
import { AuthContext, type AuthContextType, type UserInfo } from './AuthContext';
import { b2cAuthService, AccountDisabledError } from '../services/b2cAuthService';
import {
  ACCOUNT_DISABLED_EVENT,
  isAccountDisabledResponse,
} from '../services/accountDisabledFlow';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to children.
 * Handles token refresh and session expiration events.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [useAzureB2C, setUseAzureB2C] = useState(false);
  const [b2cConfig, setB2cConfig] = useState<B2CConfig | null>(null);

  /** Ref to track if auth expiration handler is registered */
  const authExpirationRegistered = useRef(false);

  /**
   * Signs the user out and sends them to the landing page with a one-shot banner
   * when the API reports account_disabled (PBI #1230).
   */
  const handleAccountDisabled = useCallback(async () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearSessionStorageForLogout();
    const didIdpLogoutRedirect = await b2cAuthService.logout({ signOutFromIdp: true });
    if (!didIdpLogoutRedirect) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  /**
   * Handle auth session expiration.
   * Called when token refresh fails and user needs to re-authenticate.
   * Clears local state and redirects to sign-in page.
   * Preserves the current URL as returnTo so user returns after re-auth.
   *
   * Business rule: Public routes (card view, landing, legal pages) must never
   * redirect to sign-in. If the user happens to have an expired token while
   * viewing a card, we silently clear stale auth state and let the public
   * page continue rendering without interruption.
   */
  const handleAuthExpiration = useCallback(() => {
    // Clear user state
    setUser(null);

    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Wipe sessionStorage (whitelist-based) so nothing user-bound from
    // the expired session leaks into the next sign-in on the same tab.
    // This covers guest scheduling drafts, the card-preview payload, the
    // admin AI-models banner cache, returnTo URLs, and anything else a
    // future feature stores in sessionStorage by default. See
    // `sessionCleanupService` for the criteria that govern the whitelist.
    clearSessionStorageForLogout();

    // On public routes, just clear stale auth state without redirecting.
    // This prevents card recipients from being sent to sign-in when an
    // expired token happens to exist (e.g., the recipient is also a user
    // whose session expired, or they share a browser with a logged-in user).
    const currentPath = window.location.pathname;
    if (isPublicPath(currentPath)) {
      console.log('[AuthContext] Session expired on public route, cleared state without redirect');
      return;
    }

    console.log('[AuthContext] Session expired, redirecting to sign-in');

    // Store current location as returnTo so user returns after re-auth
    const fullPath = currentPath + window.location.search + window.location.hash;
    if (fullPath !== AUTH_ROUTES.SIGNIN && fullPath !== AUTH_ROUTES.LANDING && sanitizeReturnToPath(fullPath)) {
      setReturnTo(fullPath);
    }

    // Navigate to sign-in
    navigate(AUTH_ROUTES.SIGNIN, { replace: true });
  }, [navigate]);

  /**
   * Register auth expiration callback with b2cAuthService.
   * This effect runs after initial render and whenever handleAuthExpiration changes.
   */
  useEffect(() => {
    if (!authExpirationRegistered.current) {
      b2cAuthService.onAuthExpiration(handleAuthExpiration);
      authExpirationRegistered.current = true;
    }

    // Cleanup: unregister callback when component unmounts
    return () => {
      b2cAuthService.clearAuthExpirationCallback();
      authExpirationRegistered.current = false;
    };
  }, [handleAuthExpiration]);

  /**
   * Listen for account_disabled responses from apiClient / b2cAuthService.
   */
  useEffect(() => {
    const onAccountDisabled = () => {
      void handleAccountDisabled();
    };

    window.addEventListener(ACCOUNT_DISABLED_EVENT, onAccountDisabled);
    return () => window.removeEventListener(ACCOUNT_DISABLED_EVENT, onAccountDisabled);
  }, [handleAccountDisabled]);

  /**
   * Persist first-touch ad attribution once the user is authenticated (PBI #1104).
   * Cleared from sessionStorage after a successful POST; not whitelisted across logout.
   */
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    void flushAcquisitionAttributionToServer();
    void flushCookieConsentToServer();
  }, [user?.id]);

  /**
   * Initialize authentication on app load
   */
  useEffect(() => {
    const initializeAuth = async () => {
      /**
       * Verify the user's token with the backend via GET /auth/me.
       * Used as a fallback when no cached user exists in localStorage.
       * On 401, clears all auth state; on other errors, preserves the
       * MSAL user with termsAccepted=false so the terms page can verify.
       */
      const verifyWithBackend = async (fallbackUser: { name: string | null; email: string }) => {
        try {
          const meResponse = await apiClient.get<UserInfo>('/auth/me');
          setUser(meResponse.data);
          localStorage.setItem('user', JSON.stringify(meResponse.data));
        } catch (error: unknown) {
          if (error instanceof AccountDisabledError || isAccountDisabledResponse(error)) {
            await handleAccountDisabled();
            return;
          }

          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 401) {
            console.log('Token invalid, clearing authentication state');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            await b2cAuthService.logout({ signOutFromIdp: false });
            setUser(null);
          } else {
            setUser({ ...fallbackUser, termsAccepted: false } as UserInfo);
          }
        }
      };

      try {
        // Use build-time config when VITE env vars are present (eliminates a
        // network round-trip per page load). Falls back to the API endpoint
        // when env vars are missing (e.g. legacy or misconfigured builds).
        const authConfig = getAuthConfigFromEnv()
          ?? (await apiClient.get<AuthModeConfig>('/auth/config')).data;

        setUseAzureB2C(authConfig.useAzureB2C);

        if (authConfig.useAzureB2C && authConfig.b2CConfig) {
          // Initialize External ID authentication (handles redirect callback and syncs user)
          setB2cConfig(authConfig.b2CConfig);
          let redirectUser: Awaited<ReturnType<typeof b2cAuthService.initialize>> = null;
          try {
            redirectUser = await b2cAuthService.initialize(authConfig.b2CConfig);
          } catch (error) {
            if (error instanceof AccountDisabledError || isAccountDisabledResponse(error)) {
              await handleAccountDisabled();
              return;
            }
            throw error;
          }

          // If user was authenticated via redirect, use the profile that
          // syncUserToBackend() already fetched from /auth/me and stored in
          // localStorage. This avoids a duplicate GET /auth/me round-trip.
          if (redirectUser) {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              try {
                setUser(JSON.parse(storedUser) as UserInfo);
              } catch {
                setUser({ ...redirectUser, termsAccepted: false });
              }
            } else {
              // syncUserToBackend failed to store user -- fall back to minimal B2C info
              setUser({ ...redirectUser, termsAccepted: false });
            }
          } else {
            // Non-redirect load: user is returning to the app with an
            // existing MSAL session (e.g. page reload, E2E page.goto()).
            const token = localStorage.getItem('token');

            if (token) {
              const b2cUser = b2cAuthService.getCurrentUser();

              if (b2cUser) {
                // Use cached user from localStorage when available. This
                // avoids a blocking GET /auth/me on every page navigation,
                // which added ~100-300ms of serial latency before React
                // could render any authenticated content.
                //
                // Safety: if the token is expired or invalid, the first
                // API call from any page component will receive a 401,
                // triggering the apiClient response interceptor to refresh
                // the token or redirect to sign-in.
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                  try {
                    setUser(JSON.parse(storedUser) as UserInfo);
                  } catch {
                    // Corrupted cache — fall back to API verification
                    await verifyWithBackend(b2cUser);
                  }
                } else {
                  // No cached user (first load after clearing cache) — must verify
                  await verifyWithBackend(b2cUser);
                }
              } else {
                // Token exists but MSAL's account cache is empty — recover
                // by verifying with the backend instead of silently leaving
                // the user logged out.
                //
                // Why this branch exists:
                // The MSAL account cache can be partially missing while the
                // 'token' key in localStorage is still valid in several
                // real-world scenarios:
                //   - Safari ITP / storage partitioning evicting some keys
                //   - Private/incognito mode quirks across browser reloads
                //   - Browser storage quota eviction (least-recently-used)
                //   - MSAL upgrades that migrate cache key formats
                //   - The user (or a browser extension) clearing only some
                //     localStorage keys
                //   - WebKit / iPhone SE emulation in Playwright after a
                //     destructive localStorage edit between navigations
                //     (the symptom that surfaced this gap on the
                //     [mobile-reset-journeys] M1 spec)
                //
                // Without this recovery path, the SPA silently does nothing
                // here, leaves `user` null, ProtectedRoute treats the user
                // as unauthenticated, and AuthStartPage triggers a fresh
                // MSAL loginRedirect — even though the existing token is
                // still valid and /auth/me would have succeeded.
                //
                // verifyWithBackend handles all three response shapes:
                //   200 → user record fetched and stored
                //   401 → full logout (token actually invalid)
                //   other → minimal user with termsAccepted=false so the
                //           accept-terms gate can re-verify
                // Pass a placeholder fallback because we genuinely have no
                // claim data to work with at this point; verifyWithBackend
                // only uses it on non-401 errors.
                await verifyWithBackend({ name: null, email: '' });
              }
            }
          }
        } else {
          // Local JWT mode - check for stored user
          const storedUser = localStorage.getItem('user');
          const token = localStorage.getItem('token');

          if (storedUser && token) {
            setUser(JSON.parse(storedUser));

            // Verify token is still valid
            try {
              const meResponse = await apiClient.get<UserInfo>('/auth/me');
              setUser(meResponse.data);
            } catch (error) {
              if (isAccountDisabledResponse(error)) {
                await handleAccountDisabled();
                return;
              }

              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
            }
          }
        }
      } catch (error) {
        if (error instanceof AccountDisabledError || isAccountDisabledResponse(error)) {
          await handleAccountDisabled();
          return;
        }

        console.error('Failed to initialize auth:', error);

        // Check if the error is a 401 - if so, don't restore from cache
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          // Token is invalid, clear everything
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          // Network error or other issue - try to restore from localStorage
          // but only if the error wasn't auth-related
          const storedUser = localStorage.getItem('user');
          const token = localStorage.getItem('token');
          if (storedUser && token) {
            setUser(JSON.parse(storedUser));
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login - Redirects to External ID's hosted login page
   *
   * For External ID mode, this redirects the user to Microsoft's login page
   * where they can sign in with email/password or social providers.
   * The email and password parameters are ignored (kept for interface compatibility).
   *
   * @param _email Ignored for External ID mode
   * @param _password Ignored for External ID mode
   * @param flow Optional auth flow: 'signin' (default) or 'signup' for direct account creation
   */
  const login = useCallback(
    async (_email: string, _password: string, flow: AuthFlow = 'signin') => {
      if (useAzureB2C) {
        if (!b2cConfig) {
          throw new Error('Authentication configuration not ready. Please wait a moment and try again.');
        }

        // Ensure MSAL is initialized
        if (!b2cAuthService.isInitialized()) {
          await b2cAuthService.initialize(b2cConfig);
        }

        // Redirect to External ID's login page with the specified flow
        await b2cAuthService.login(flow);
      } else {
        throw new Error('Local JWT authentication is not enabled. Please configure Azure External ID.');
      }
    },
    [useAzureB2C, b2cConfig]
  );

  /**
   * Logout the current user
   *
   * Clears local app state, MSAL cache, every non-whitelisted
   * sessionStorage entry, and navigates to the landing page. The next
   * login will require fresh authentication (no SSO auto-login).
   *
   * Why sessionStorage is wiped (not just specific keys):
   * sessionStorage survives same-tab logout + login. Anything the user
   * left there — guest scheduling drafts, card preview payloads, admin
   * banner caches, returnTo URLs — was bound to the session that just
   * ended and could silently leak into the next account that signs in.
   * The original symptom that surfaced this hole was a guest flower
   * order draft from a fresh sign-up auto-confirming against the
   * user's existing account after they logged out and back in on the
   * same incognito tab. A whitelist-based wipe (see
   * `sessionCleanupService`) makes "leaks across an account switch"
   * structurally impossible and forces any future feature that needs
   * cross-logout persistence to opt in explicitly.
   */
  const logout = useCallback(async () => {
    setUser(null);

    // See comment above for the rationale.
    clearSessionStorageForLogout();

    // Clear all local state and MSAL cache
    const didIdpLogoutRedirect = await b2cAuthService.logout({ signOutFromIdp: true });

    // logoutRedirect sends the browser to Entra and back to origin; skip SPA navigate.
    if (!didIdpLogoutRedirect) {
      navigate('/');
    }
  }, [navigate]);

  /**
   * Refresh user information from the backend.
   * Called after accepting terms to update the termsAccepted flag.
   */
  const refreshUser = useCallback(async () => {
    try {
      const meResponse = await apiClient.get<UserInfo>('/auth/me');
      setUser(meResponse.data);

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(meResponse.data));
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  }, []);

  // Compute role-based properties
  const roles = useMemo(() => user?.roles ?? [], [user?.roles]);
  const isAdmin = useMemo(() => hasAnyAdminRole(roles), [roles]);
  const isSuperAdmin = useMemo(() => roles.includes(SUPER_ADMIN), [roles]);
  const canManageContent = useMemo(() => hasContentManagementAccess(roles), [roles]);
  const canAccessSupport = useMemo(() => hasSupportAccess(roles), [roles]);

  const hasRole = useCallback(
    (role: string) => roles.includes(role),
    [roles]
  );

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    termsAccepted: user?.termsAccepted ?? false,
    useAzureB2C,
    b2cConfig,
    roles,
    isAdmin,
    isSuperAdmin,
    canManageContent,
    canAccessSupport,
    hasRole,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
